import { type FC, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Divider,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Radio,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useTranslation } from "@/shared/hooks";
import {
  areResultsRevealed,
  correctOptionIds,
  isGuessed,
  isPollClosed,
  pollPercent,
} from "../lib/pollResults";
import type { PollHostType, SitePoll } from "../model/types";

export interface PollCardProps {
  poll: SitePoll;
  hostType: PollHostType;
  /** frame: левый нижний угол кадра (десктоп), slot: мобильный слот. */
  placement: "frame" | "slot";
  voting: boolean;
  /** Последний голос за этот опрос не ушёл. */
  voteFailed?: boolean;
  defaultExpanded?: boolean;
  /** false: без своей шапки (вопрос и крестик рисует DialogHeader диалога). */
  withHeader?: boolean;
  onVote: (optionIds: string[]) => void;
  onDismiss: () => void;
}

export const PollCard: FC<PollCardProps> = ({
  poll,
  hostType,
  placement,
  voting,
  voteFailed = false,
  defaultExpanded = false,
  withHeader = true,
  onVote,
  onDismiss,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(defaultExpanded);
  const [selected, setSelected] = useState<string[]>([]);

  const hasVoted = Boolean(poll.viewerVote?.length);
  const closed = isPollClosed(poll, hostType);
  const revealed = areResultsRevealed(poll);
  const canVote = !hasVoted && !closed;
  // На эфире правильный ответ подсвечивается после закрытия, на записи
  // сразу после голоса (момента закрытия у записи нет).
  const showCorrect =
    revealed &&
    correctOptionIds(poll).length > 0 &&
    (closed || hostType === "VIDEO");
  const guessed = hasVoted && isGuessed(poll);
  const correctTexts = poll.options
    .filter((option) => option.isCorrect === true)
    .map((option) => option.text)
    .join(", ");

  const toggle = (optionId: string) =>
    setSelected((prev) => {
      if (!poll.isMultiple) return [optionId];
      return prev.includes(optionId)
        ? prev.filter((id) => id !== optionId)
        : [...prev, optionId];
    });

  // frame: карточка в кадре; slot со своей шапкой: отдельная карточка;
  // без шапки: тело диалога, фон и отступы даёт сам диалог.
  let paperSx: SxProps<Theme> = { p: 0, backgroundColor: "transparent" };
  if (placement === "frame") {
    paperSx = {
      position: "absolute",
      left: 12,
      // На одной высоте с панелью реакций, выше сик-бара.
      bottom: 96,
      zIndex: 210,
      width: 320,
      maxWidth: "calc(100% - 24px)",
      p: 1.5,
      borderRadius: 2,
    };
  } else if (withHeader) {
    paperSx = { p: 1.5, borderRadius: 2 };
  }

  return (
    <Paper elevation={placement === "frame" ? 6 : 0} sx={paperSx}>
      {withHeader && (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <Typography variant="subtitle2" sx={{ flex: 1 }}>
            {poll.question}
          </Typography>
          <IconButton
            size="small"
            aria-label={t("polls.dismiss")}
            onClick={onDismiss}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {canVote && !expanded && (
        <Button
          size="small"
          variant="contained"
          disableElevation
          onClick={() => setExpanded(true)}
          sx={{ mt: 1 }}
        >
          {t("polls.takePoll")}
        </Button>
      )}

      {canVote && expanded && (
        <Box sx={{ mt: withHeader ? 1.5 : 0 }}>
          {poll.isMultiple && (
            <Typography variant="caption" color="text.secondary">
              {t("polls.multipleHint")}
            </Typography>
          )}
          <List
            role={poll.isMultiple ? "group" : "radiogroup"}
            sx={{ display: "flex", flexDirection: "column", gap: 1, py: 1 }}
          >
            {poll.options.map((option) => {
              const checked = selected.includes(option.id);
              return (
                <ListItemButton
                  key={option.id}
                  selected={checked}
                  onClick={() => toggle(option.id)}
                  role={poll.isMultiple ? "checkbox" : "radio"}
                  aria-checked={checked}
                  sx={{
                    borderRadius: 2,
                    border: 1,
                    borderColor: checked ? "primary.main" : "divider",
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {poll.isMultiple ? (
                      <Checkbox
                        edge="start"
                        checked={checked}
                        tabIndex={-1}
                        disableRipple
                        size="small"
                      />
                    ) : (
                      <Radio
                        edge="start"
                        checked={checked}
                        tabIndex={-1}
                        disableRipple
                        size="small"
                      />
                    )}
                  </ListItemIcon>
                  <ListItemText
                    primary={option.text}
                    slotProps={{
                      primary: { fontWeight: checked ? 600 : 400 },
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
          <Button
            fullWidth
            size="large"
            variant="contained"
            disableElevation
            disabled={voting || selected.length === 0}
            onClick={() => onVote(selected)}
          >
            {t("polls.submit")}
          </Button>
          {voteFailed && (
            <Typography
              variant="caption"
              color="error"
              role="alert"
              sx={{ display: "block", mt: 1 }}
            >
              {t("polls.voteFailed")}
            </Typography>
          )}
        </Box>
      )}

      {!canVote && revealed && (
        <Box sx={{ mt: withHeader ? 1 : 0 }}>
          <List disablePadding>
            {poll.options.map((option) => {
              const percent = pollPercent(option.votes, poll.votersCount);
              const correct = showCorrect && option.isCorrect === true;
              const mine = Boolean(poll.viewerVote?.includes(option.id));
              return (
                <ListItem key={option.id} disableGutters sx={{ py: 1 }}>
                  <ListItemText
                    disableTypography
                    primary={
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {mine && (
                          <CheckCircleIcon
                            fontSize="small"
                            color={correct ? "success" : "primary"}
                            aria-hidden
                          />
                        )}
                        <Typography
                          variant="body1"
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            fontWeight: mine ? 700 : 500,
                            color: correct ? "success.main" : undefined,
                          }}
                        >
                          {option.text}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 700,
                            fontVariantNumeric: "tabular-nums",
                          }}
                        >
                          {percent}%
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <LinearProgress
                        variant="determinate"
                        value={percent}
                        color={correct ? "success" : "primary"}
                        sx={{
                          mt: 1,
                          height: 8,
                          borderRadius: 4,
                          "& .MuiLinearProgress-bar": { borderRadius: 4 },
                        }}
                      />
                    }
                  />
                </ListItem>
              );
            })}
          </List>
          <Divider sx={{ my: 1.5 }} />
          {showCorrect && guessed && (
            <Typography variant="body2" color="success.main" fontWeight={600}>
              {t("polls.youGuessed")}
            </Typography>
          )}
          {showCorrect && !guessed && (
            <Typography variant="body2" fontWeight={600}>
              {t("polls.correctAnswer", { answer: correctTexts })}
            </Typography>
          )}
          <Typography variant="caption" color="text.secondary">
            {t("polls.votersCount", { count: poll.votersCount ?? 0 })}
          </Typography>
        </Box>
      )}

      {!canVote && !revealed && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: withHeader ? 1.5 : 0 }}
        >
          {closed ? t("polls.closed") : t("polls.resultsAfterClose")}
        </Typography>
      )}
    </Paper>
  );
};
